/**
 * VM-WORKSPACE-3 — Workspace Template schema.
 *
 * Templates are anonymized workspace snapshots that can be published per-tenant
 * or to the Kairos public network. They contain no live tenant data — only
 * the panel layout and metadata.
 *
 * Network-scoped aggregation of template usage is a DATA-FLYWHEEL-1 substrate
 * concern (operator-zone); this schema ships the surface.
 */
import { z } from 'zod';
import { WorkspaceGridLayoutSchema, PanelPlacementSchema } from './workspace';

export const TEMPLATE_VISIBILITY = Object.freeze(['tenant', 'public']);
export const TemplateVisibilitySchema = z.enum(TEMPLATE_VISIBILITY);

export const WorkspaceTemplateSchema = z.object({
  id:           z.string().min(1),
  tenantId:     z.string().min(1),
  authorId:     z.string().min(1),
  name:         z.string().min(1).max(120),
  description:  z.string().max(1000).optional(),
  tags:         z.array(z.string().max(40)).max(10).default([]),
  visibility:   TemplateVisibilitySchema.default('tenant'),
  gridLayout:   WorkspaceGridLayoutSchema,
  panels:       z.array(PanelPlacementSchema).default([]),
  // Attribution chain for remixed templates
  remixedFromId: z.string().optional(),
  remixedFromName: z.string().optional(),
  // Stats (server-populated; client treats as read-only)
  importCount:  z.number().int().min(0).default(0),
  // Lifecycle
  createdAt:    z.union([z.string(), z.object({})]).optional(),
  updatedAt:    z.union([z.string(), z.object({})]).optional(),
  softDeleted:  z.boolean().optional(),
  schemaVersion: z.number().int().default(1),
}).passthrough();

/** @typedef {import('zod').infer<typeof WorkspaceTemplateSchema>} WorkspaceTemplate */

export const WORKSPACE_TEMPLATE_COLLECTION = 'workspace_templates';
